import { loadStripe } from '@stripe/stripe-js/pure';
import { Button, Dialog, Input, Select } from '@wonderschool/common-base-ui';
import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { functions } from '../../../api/firebase/firebase';
import { logError } from '../../../rollbar';

interface Props {
  organizationId: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function CustomDepositAccountForm({ organizationId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [holderName, setHolderName] = useState('');
  const [holderType, setHolderType] = useState<'individual' | 'company'>();
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Reuse the token after an uncertain network result. The server uses it as its
  // idempotency key, so retrying cannot create another bank account.
  const token = useRef<string | null>(null);
  const inFlight = useRef(false);

  const holderOptions = [
    { value: 'individual' as const, label: t('billing.depositAccount.individual') },
    { value: 'company' as const, label: t('billing.depositAccount.company') },
  ];

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    setError('');
    if (!holderType) {
      setError(t('billing.depositAccount.selectHolderType'));
      return;
    }
    if (!holderName.trim() || !/^\d{9}$/.test(routingNumber) || !/^\d{4,17}$/.test(accountNumber)) {
      setError(t('billing.depositAccount.invalidDetails'));
      return;
    }
    if (accountNumber !== confirmation) {
      setError(t('billing.depositAccount.accountMismatch'));
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    try {
      if (!token.current) {
        const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        if (!key) throw new Error('Stripe publishable key is missing');
        const stripe = await loadStripe(key);
        if (!stripe) throw new Error('Stripe did not load');
        const result = await stripe.createToken('bank_account', {
          country: 'US',
          currency: 'usd',
          account_holder_name: holderName.trim(),
          account_holder_type: holderType,
          routing_number: routingNumber,
          account_number: accountNumber,
        });
        if (result.error || !result.token) {
          setError(t('billing.depositAccount.invalidDetails'));
          return;
        }
        token.current = result.token.id;
      }

      await functions().httpsCallable('callables-stripe-updateCustomPayoutAccount')({
        organizationId,
        bankAccountToken: token.current,
      });
      onSaved();
    } catch {
      // Never pass a Stripe error, bank fields, or token to telemetry.
      logError('Unable to update custom deposit account', undefined, { organizationId });
      setError(t('billing.depositAccount.saveError'));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog isOpen onClose={() => !inFlight.current && onClose()} hasCloseButton>
      <Dialog.Panel>
        <Dialog.Title>{t('billing.depositAccount.title')}</Dialog.Title>
        <Dialog.Description>{t('billing.depositAccount.description')}</Dialog.Description>
        <form
          onSubmit={handleSubmit}
          onChange={() => {
            token.current = null;
          }}
          className="mt-4 flex flex-col gap-4"
          autoComplete="off"
          data-testid="custom-deposit-account-form"
        >
          <Input
            name="bankHolderName"
            label={t('billing.depositAccount.holderName')}
            value={holderName}
            onChange={(event) => setHolderName(event.target.value)}
            required
            maxLength={100}
            disabled={submitting}
          />
          <Select
            name="bankHolderType"
            label={t('billing.depositAccount.holderType')}
            placeholder={t('billing.depositAccount.selectHolderType')}
            options={holderOptions}
            required
            disabled={submitting}
            onChange={(option) => {
              const value = option?.value;
              setHolderType(value === 'individual' || value === 'company' ? value : undefined);
              token.current = null;
            }}
          />
          <Input
            label={t('billing.depositAccount.routingNumber')}
            name="bankRoutingNumber"
            value={routingNumber}
            onChange={(event) => setRoutingNumber(event.target.value)}
            inputMode="numeric"
            maxLength={9}
            required
            disabled={submitting}
          />
          <Input
            label={t('billing.depositAccount.accountNumber')}
            name="bankAccountNumber"
            value={accountNumber}
            onChange={(event) => setAccountNumber(event.target.value)}
            type="password"
            inputMode="numeric"
            maxLength={17}
            required
            disabled={submitting}
            autoComplete="new-password"
          />
          <Input
            label={t('billing.depositAccount.confirmAccountNumber')}
            name="bankAccountConfirmation"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            type="password"
            inputMode="numeric"
            maxLength={17}
            required
            disabled={submitting}
            autoComplete="new-password"
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <p className="text-sm text-gray-600">
            {t('billing.depositAccount.supportPrompt')}{' '}
            <a href="mailto:support@wonderschool.com" className="text-blue-700 underline">
              {t('support@wonderschool.com')}
            </a>
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" label={t('common.cancel')} onClick={onClose} disabled={submitting} />
            <Button
              type="submit"
              primary
              label={t(submitting ? 'common.loading' : 'billing.depositAccount.save')}
              disabled={submitting}
            />
          </div>
        </form>
      </Dialog.Panel>
    </Dialog>
  );
}
